import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, IndianRupee, Clock, CheckCircle, ChevronDown, ChevronUp, ArrowLeft, Truck, RefreshCw, Hand, Printer, MapPin, Search, ChevronLeft, ChevronRight, Copy, ExternalLink, ClipboardCheck, ShieldCheck, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

const STATUS_OPTIONS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  processing: 'bg-purple-100 text-purple-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PAYMENT_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const FULFILLMENT_COLORS = {
  pending_admin_approval: 'bg-amber-100 text-amber-900 ring-1 ring-amber-300',
  approved: 'bg-sky-100 text-sky-800',
  submitted: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  needs_attention: 'bg-red-100 text-red-800 ring-1 ring-red-300',  // e.g. print file too low-res
  no_mapping: 'bg-orange-100 text-orange-800',
  not_configured: 'bg-gray-100 text-gray-800',
  manual: 'bg-amber-100 text-amber-800',
  pending_manual: 'bg-amber-100 text-amber-800',
  printed: 'bg-cyan-100 text-cyan-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  awaiting_payment: 'bg-gray-100 text-gray-800',
  none: 'bg-gray-100 text-gray-800',
};

const PROVIDER_LABELS = {
  prodigi: { label: 'Prodigi', color: 'bg-violet-100 text-violet-800' },
  printful: { label: 'Printful (Legacy)', color: 'bg-gray-100 text-gray-800' },
  qikink: { label: 'Qikink', color: 'bg-orange-100 text-orange-800' },
  manual: { label: 'Manual', color: 'bg-amber-100 text-amber-800' },
};

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const INDIAN_POD_LINKS = [
  { name: 'Qikink', url: 'https://www.qikink.com/', color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
  { name: 'Printposters', url: 'https://printposters.in/', color: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100' },
  { name: 'Printo', url: 'https://www.printo.in/', color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100' },
];

const formatAddressText = (addr) => {
  if (!addr) return '';
  const lines = [
    addr.address_line1,
    addr.address_line2,
    [addr.city, addr.state, addr.pincode].filter(Boolean).join(', '),
    addr.country,
  ].filter(Boolean);
  return lines.join('\n');
};

const formatOrderDetailsText = (order) => {
  const lines = ['=== ORDER DETAILS ==='];
  lines.push(`Order: ${order.order_number}`);
  lines.push(`Date: ${formatDate(order.created_at)}`);
  lines.push('');
  lines.push('--- CUSTOMER ---');
  lines.push(`Name: ${order.customer_name}`);
  lines.push(`Email: ${order.customer_email}`);
  if (order.customer_phone) lines.push(`Phone: ${order.customer_phone}`);
  lines.push('');
  lines.push('--- SHIPPING ADDRESS ---');
  if (order.shipping_address) {
    lines.push(formatAddressText(order.shipping_address));
  }
  lines.push('');
  lines.push('--- ITEMS ---');
  order.items.forEach((item, i) => {
    lines.push(`${i + 1}. ${item.title}`);
    if (item.size_label) lines.push(`   Size: ${item.size_label}`);
    if (item.paper_label) lines.push(`   Paper: ${item.paper_label}`);
    if (item.frame_label) lines.push(`   Frame: ${item.frame_label}`);
    lines.push(`   Qty: ${item.quantity || 1} x ${formatPrice(item.unit_price || item.price)}`);
  });
  lines.push('');
  lines.push('--- TOTAL ---');
  lines.push(`Subtotal: ${formatPrice(order.subtotal)}`);
  lines.push(`Shipping: ${formatPrice(order.shipping_cost)}`);
  lines.push(`Total: ${formatPrice(order.total)}`);
  return lines.join('\n');
};

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [retryingOrder, setRetryingOrder] = useState(null);
  const [manualAction, setManualAction] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [approvingOrder, setApprovingOrder] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);
  const fileInputRef = useRef(null);
  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [filterType, setFilterType] = useState('');
  // Stats (fetched once without pagination)
  const [stats, setStats] = useState({ revenue: 0, total: 0, pending: 0, paid: 0 });
  const navigate = useNavigate();

  const handleCopy = useCallback(async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchStats();
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const res = await axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } });
      const all = res.data.items || res.data;
      setStats({
        revenue: all.filter(o => o.payment_status === 'paid').reduce((s, o) => s + o.total, 0),
        total: all.length,
        pending: all.filter(o => o.order_status === 'pending' || o.order_status === 'confirmed').length,
        paid: all.filter(o => o.payment_status === 'paid').length,
      });
    } catch { /* stats fetch non-critical */ }
  };

  const fetchOrders = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterPayment) params.set('payment', filterPayment);
      if (filterType) params.set('order_type', filterType);

      const res = await axios.get(`${API}/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;
      if (data.items) {
        setOrders(data.items);
        setTotalPages(data.pages);
        setTotal(data.total);
      } else {
        setOrders(Array.isArray(data) ? data : []);
        setTotalPages(1);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, filterPayment, filterType]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [debouncedSearch, filterPayment, filterType]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    setUpdatingStatus(orderId);
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      await axios.put(`${API}/orders/${orderId}/status`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, order_status: newStatus } : o));
      toast.success(`Order updated to ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update order status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleRetryFulfillment = async (orderId) => {
    const token = localStorage.getItem('admin_token');
    setRetryingOrder(orderId);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/retry-fulfillment`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(prev => prev.map(o => o.id === orderId ? {
        ...o,
        fulfillment_status: res.data.fulfillment_status,
        fulfillment_note: res.data.fulfillment_note,
        fulfillment_provider: res.data.fulfillment_provider,
      } : o));
      toast.success(res.data.fulfillment_status === 'submitted'
        ? `Submitted to ${res.data.fulfillment_provider}!`
        : `Fulfillment: ${res.data.fulfillment_status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to retry');
    } finally {
      setRetryingOrder(null);
    }
  };

  const handleManualAction = async (orderId, action, extra = {}) => {
    const token = localStorage.getItem('admin_token');
    setManualAction(orderId);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/manual-fulfillment`, { action, ...extra }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOrders();
      toast.success(res.data.message);
      setTrackingNumber('');
      setCarrier('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setManualAction(null);
    }
  };

  const handleApproveOrder = async (orderId) => {
    const token = localStorage.getItem('admin_token');
    setApprovingOrder(orderId);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOrders();
      toast.success(res.data.fulfillment_status === 'submitted'
        ? `Approved & submitted to ${res.data.fulfillment_provider}!`
        : res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve order');
    } finally {
      setApprovingOrder(null);
    }
  };

  const handleUploadPrintImage = async (orderId, file) => {
    const token = localStorage.getItem('admin_token');
    setUploadingImage(orderId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/orders/${orderId}/upload-print-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      await fetchOrders();
      toast.success(`Print image uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleDeletePrintImage = async (orderId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/orders/${orderId}/print-image`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOrders();
      toast.success('Custom print image removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove image');
    }
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="order-management-page">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between mb-10">
          <div>
            <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-2 transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft size={16} /> Back to Dashboard
            </button>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight" data-testid="order-management-title">
              Order Management
            </h1>
          </div>
        </div>

        {/* Revenue Cards — only show when there are orders */}
        {stats.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {[
            { icon: IndianRupee, label: 'Total Revenue', value: formatPrice(stats.revenue), tid: 'stat-total-revenue' },
            { icon: Package, label: 'Total Orders', value: stats.total, tid: 'stat-total-orders' },
            { icon: Clock, label: 'Pending', value: stats.pending, tid: 'stat-pending-orders' },
            { icon: CheckCircle, label: 'Paid', value: stats.paid, tid: 'stat-paid-orders' },
          ].map(({ icon: Icon, label, value, tid }) => (
            <div key={tid} className="bg-card border border-border rounded-lg p-3 sm:p-5" data-testid={tid}>
              <div className="flex items-center gap-1.5 sm:gap-2 text-muted-foreground mb-1.5 sm:mb-2">
                <Icon size={16} />
                <span className="font-sans text-[10px] sm:text-xs uppercase tracking-[0.15em]">{label}</span>
              </div>
              <p className="font-serif text-xl sm:text-2xl md:text-3xl font-light">{value}</p>
            </div>
          ))}
        </div>
        )}

        {/* Search & Filters — only show when there are orders */}
        {stats.total > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input
              type="text" placeholder="Search orders, customers..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} data-testid="order-search-input"
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>
          <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}
            data-testid="filter-payment"
            className="px-3 py-2 bg-card border border-border rounded-lg font-sans text-sm">
            <option value="">All Payments</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            data-testid="filter-type"
            className="px-3 py-2 bg-card border border-border rounded-lg font-sans text-sm">
            <option value="">All Types</option>
            <option value="print">Print</option>
            <option value="digital">Digital</option>
            <option value="original">Original</option>
          </select>
          <span className="text-xs text-muted-foreground font-sans">{total} order{total !== 1 ? 's' : ''}</span>
        </div>
        )}

        {/* Orders Table */}
        {loading ? (
          <p className="text-center text-muted-foreground py-12 font-sans">Loading orders...</p>
        ) : orders.length === 0 && stats.total === 0 ? (
          <div className="text-center py-24 px-6" data-testid="no-orders-message">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-muted/50 flex items-center justify-center">
              <Package size={28} className="text-muted-foreground" />
            </div>
            <p className="font-serif text-2xl sm:text-3xl font-light mb-3">No orders yet</p>
            <p className="text-muted-foreground font-sans text-sm max-w-md mx-auto leading-relaxed">
              When customers purchase prints, originals, or digital downloads from your gallery, their orders will appear here for you to manage and fulfill.
            </p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16" data-testid="no-filtered-orders">
            <Package size={36} className="mx-auto mb-3 text-muted-foreground" />
            <p className="font-serif text-xl font-light mb-2">No matching orders</p>
            <p className="text-muted-foreground font-sans text-sm">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id} className="bg-card border border-border rounded-lg overflow-hidden" data-testid={`order-row-${order.id}`}>
                <div
                  className="flex flex-wrap items-center gap-4 p-5 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                  data-testid={`order-toggle-${order.id}`}
                >
                  <div className="flex-1 min-w-[140px]">
                    <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Order</p>
                    <p className="font-mono text-sm font-medium" data-testid={`order-number-${order.id}`}>{order.order_number}</p>
                  </div>
                  <div className="flex-1 min-w-[120px]">
                    <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Customer</p>
                    <p className="font-sans text-sm">{order.customer_name}</p>
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Total</p>
                    <p className="font-serif text-lg font-light">{formatPrice(order.total)}</p>
                  </div>
                  <div className="flex-1 min-w-[100px]">
                    <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Payment</p>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${PAYMENT_COLORS[order.payment_status] || 'bg-gray-100 text-gray-800'}`} data-testid={`payment-status-${order.id}`}>
                      {order.payment_status}
                    </span>
                  </div>
                  {order.order_type === 'print' && (
                    <>
                      <div className="flex-1 min-w-[120px]">
                        <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Fulfillment</p>
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${FULFILLMENT_COLORS[order.fulfillment_status] || 'bg-gray-100 text-gray-800'}`}
                          data-testid={`fulfillment-status-${order.id}`}>
                          {order.fulfillment_status === 'pending_admin_approval' ? 'Needs Approval' : order.fulfillment_status || 'awaiting_payment'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-[90px]">
                        <p className="font-sans text-xs text-muted-foreground uppercase tracking-wider">Provider</p>
                        {order.fulfillment_provider ? (
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${(PROVIDER_LABELS[order.fulfillment_provider] || {}).color || 'bg-gray-100 text-gray-800'}`}>
                            {(PROVIDER_LABELS[order.fulfillment_provider] || {}).label || order.fulfillment_provider}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Auto</span>
                        )}
                      </div>
                    </>
                  )}
                  <div className="flex-shrink-0">
                    {expandedOrder === order.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {expandedOrder === order.id && (
                  <div className="border-t border-border p-5 bg-muted/10 space-y-6" data-testid={`order-details-${order.id}`}>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Items Ordered</h4>
                        <div className="space-y-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex gap-3">
                              {item.image_url && <img src={item.image_url} alt={item.title} className="w-12 h-12 object-cover rounded" />}
                              <div>
                                <p className="font-sans text-sm font-medium">{item.title}</p>
                                <p className="font-sans text-xs text-muted-foreground">Qty: {item.quantity || 1} &middot; {formatPrice(item.unit_price || item.price)}</p>
                                {item.size_label && <p className="font-sans text-xs text-muted-foreground">{item.size_label} &middot; {item.paper_label} &middot; {item.frame_label}</p>}
                              </div>
                            </div>
                          ))}
                        </div>

                        {order.order_type === 'print' && (
                          <div className="mt-4 pt-3 border-t border-border">
                            <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5">
                              <Truck size={13} /> Fulfillment
                              {order.fulfillment_provider && (
                                <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${(PROVIDER_LABELS[order.fulfillment_provider] || {}).color || ''}`}>
                                  {(PROVIDER_LABELS[order.fulfillment_provider] || {}).label || order.fulfillment_provider}
                                </span>
                              )}
                            </h4>
                            <p className="font-sans text-xs text-muted-foreground mb-1">
                              Status: <span className={`font-medium ${order.fulfillment_status === 'pending_admin_approval' ? 'text-amber-800' : order.fulfillment_status === 'submitted' ? 'text-blue-700' : order.fulfillment_status === 'failed' ? 'text-red-600' : order.fulfillment_status === 'delivered' ? 'text-green-700' : 'text-foreground'}`}>
                                {order.fulfillment_status === 'pending_admin_approval' ? 'Awaiting Admin Approval' : order.fulfillment_status || 'awaiting payment'}
                              </span>
                            </p>
                            {order.fulfillment_note && <p className="font-sans text-xs text-muted-foreground mb-2">{order.fulfillment_note}</p>}
                            {order.prodigi_order_id && <p className="font-sans text-xs text-muted-foreground">Prodigi ID: {order.prodigi_order_id}</p>}
                            {order.printful_order_id && <p className="font-sans text-xs text-muted-foreground">Printful ID: #{order.printful_order_id}</p>}
                            {order.qikink_order_id && <p className="font-sans text-xs text-muted-foreground">Qikink ID: #{order.qikink_order_id}</p>}
                            {order.tracking_number && (
                              <p className="font-sans text-xs font-medium text-indigo-700 mt-1">
                                <MapPin size={11} className="inline mr-1" />Tracking: {order.tracking_number} {order.shipping_carrier && `(${order.shipping_carrier})`}
                              </p>
                            )}

                            <div className="flex flex-wrap gap-2 mt-3">
                              {/* ─── APPROVAL GATE (P0) ─── */}
                              {order.fulfillment_status === 'pending_admin_approval' && order.payment_status === 'paid' && (
                                <div className="w-full space-y-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50/80" data-testid={`approval-gate-${order.id}`}>
                                  <div className="flex items-center gap-2">
                                    <ShieldCheck size={16} className="text-amber-700" />
                                    <span className="font-sans text-sm font-semibold text-amber-900">Admin Approval Required</span>
                                  </div>
                                  <p className="font-sans text-xs text-amber-800">
                                    Payment verified. Upload a custom print image (optional) or approve to send directly to the POD provider.
                                  </p>

                                  {/* Custom Print Image Upload */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {order.custom_print_image_name ? (
                                      <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-md">
                                        <ImageIcon size={14} className="text-green-700" />
                                        <span className="font-sans text-xs text-green-800 font-medium">{order.custom_print_image_name}</span>
                                        <button onClick={(e) => { e.stopPropagation(); handleDeletePrintImage(order.id); }}
                                          className="ml-1 p-0.5 rounded hover:bg-red-100 transition-colors" data-testid={`remove-print-image-${order.id}`}>
                                          <X size={12} className="text-red-500" />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <input type="file" accept="image/*" ref={fileInputRef} className="hidden"
                                          onChange={(e) => { if (e.target.files[0]) handleUploadPrintImage(order.id, e.target.files[0]); }}
                                          data-testid={`print-image-input-${order.id}`} />
                                        <Button variant="outline" size="sm"
                                          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                          disabled={uploadingImage === order.id}
                                          data-testid={`upload-print-image-${order.id}`}>
                                          <Upload size={12} className="mr-1.5" />
                                          {uploadingImage === order.id ? 'Uploading...' : 'Upload Print Image'}
                                        </Button>
                                      </>
                                    )}
                                  </div>

                                  <div className="flex gap-2">
                                    <Button variant="default" size="sm"
                                      className="bg-green-700 hover:bg-green-800 text-white"
                                      onClick={(e) => { e.stopPropagation(); handleApproveOrder(order.id); }}
                                      disabled={approvingOrder === order.id}
                                      data-testid={`approve-order-${order.id}`}>
                                      <ShieldCheck size={14} className="mr-1.5" />
                                      {approvingOrder === order.id ? 'Approving...' : 'Approve & Send to POD'}
                                    </Button>
                                    <Button variant="outline" size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleManualAction(order.id, 'mark_manual'); }}
                                      disabled={manualAction === order.id}
                                      data-testid={`manual-instead-${order.id}`}>
                                      <Hand size={12} className="mr-1.5" /> Manual Instead
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {['failed', 'no_mapping', 'awaiting_payment', 'needs_attention'].includes(order.fulfillment_status) && (
                                <Button variant="outline" size="sm" onClick={() => handleRetryFulfillment(order.id)} disabled={retryingOrder === order.id} data-testid={`retry-fulfillment-${order.id}`}>
                                  <RefreshCw size={12} className={`mr-1.5 ${retryingOrder === order.id ? 'animate-spin' : ''}`} />
                                  {retryingOrder === order.id ? 'Retrying...' : 'Auto-Fulfill'}
                                </Button>
                              )}
                              {!['pending_admin_approval','manual','printed','shipped','delivered'].includes(order.fulfillment_status) && (
                                <Button variant="outline" size="sm" onClick={() => handleManualAction(order.id, 'mark_manual')} disabled={manualAction === order.id} data-testid={`manual-fulfill-${order.id}`}>
                                  <Hand size={12} className="mr-1.5" /> Manual Fulfill
                                </Button>
                              )}
                              {order.fulfillment_status === 'manual' && (
                                <Button variant="outline" size="sm" onClick={() => handleManualAction(order.id, 'mark_printed')} disabled={manualAction === order.id} data-testid={`mark-printed-${order.id}`}>
                                  <Printer size={12} className="mr-1.5" /> Mark Printed
                                </Button>
                              )}
                              {(order.fulfillment_status === 'printed' || (order.fulfillment_provider === 'manual' && order.fulfillment_status === 'manual')) && (
                                <div className="flex items-center gap-2 w-full mt-1">
                                  <input type="text" placeholder="Tracking number" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)}
                                    className="flex-1 px-3 py-1.5 bg-background border border-border rounded text-xs" data-testid={`tracking-input-${order.id}`} />
                                  <input type="text" placeholder="Carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)}
                                    className="w-24 px-3 py-1.5 bg-background border border-border rounded text-xs" data-testid={`carrier-input-${order.id}`} />
                                  <Button variant="default" size="sm" onClick={() => handleManualAction(order.id, 'mark_shipped', { tracking_number: trackingNumber, carrier })}
                                    disabled={manualAction === order.id} data-testid={`mark-shipped-${order.id}`}>
                                    <Truck size={12} className="mr-1.5" /> Ship
                                  </Button>
                                </div>
                              )}
                              {order.fulfillment_status === 'shipped' && order.fulfillment_provider === 'manual' && (
                                <Button variant="outline" size="sm" onClick={() => handleManualAction(order.id, 'mark_delivered')} disabled={manualAction === order.id} data-testid={`mark-delivered-${order.id}`}>
                                  <CheckCircle size={12} className="mr-1.5" /> Mark Delivered
                                </Button>
                              )}
                            </div>

                            {/* Manual Fulfillment Tools — Copy & Quick Links */}
                            {order.fulfillment_provider === 'manual' && (
                              <div className="mt-4 pt-3 border-t border-border/60" data-testid={`manual-tools-${order.id}`}>
                                <h5 className="font-sans text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2.5">Quick Actions</h5>
                                <div className="flex flex-wrap gap-2 mb-3">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleCopy(formatOrderDetailsText(order), `details-${order.id}`); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
                                    data-testid={`copy-order-details-${order.id}`}
                                  >
                                    {copiedField === `details-${order.id}` ? <ClipboardCheck size={12} className="text-green-600" /> : <Copy size={12} />}
                                    {copiedField === `details-${order.id}` ? 'Copied!' : 'Copy Full Order Details'}
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const addr = order.shipping_address;
                                      const itemInfo = order.items.map(i => `${i.title} (${i.size_label || ''}, ${i.paper_label || ''}, ${i.frame_label || ''})`).join('; ');
                                      handleCopy(`${order.customer_name}\n${addr?.address_line1 || ''}\n${[addr?.city, addr?.state, addr?.pincode].filter(Boolean).join(', ')}\n${addr?.country || ''}\nPhone: ${order.customer_phone || ''}\nItems: ${itemInfo}`, `quick-${order.id}`);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-muted/40 hover:bg-muted/70 transition-colors"
                                    data-testid={`copy-quick-summary-${order.id}`}
                                  >
                                    {copiedField === `quick-${order.id}` ? <ClipboardCheck size={12} className="text-green-600" /> : <Copy size={12} />}
                                    {copiedField === `quick-${order.id}` ? 'Copied!' : 'Copy Quick Summary'}
                                  </button>
                                </div>
                                <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Place Order On</p>
                                <div className="flex flex-wrap gap-2">
                                  {INDIAN_POD_LINKS.map(({ name, url, color }) => (
                                    <a
                                      key={name}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${color}`}
                                      data-testid={`pod-link-${name.toLowerCase()}-${order.id}`}
                                    >
                                      <ExternalLink size={11} /> {name}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground">Shipping Address</h4>
                          {order.shipping_address && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCopy(formatAddressText(order.shipping_address), `addr-${order.id}`); }}
                              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-border hover:bg-muted/50 transition-colors"
                              data-testid={`copy-address-${order.id}`}
                            >
                              {copiedField === `addr-${order.id}` ? <ClipboardCheck size={11} className="text-green-600" /> : <Copy size={11} />}
                              {copiedField === `addr-${order.id}` ? 'Copied' : 'Copy Address'}
                            </button>
                          )}
                        </div>
                        {order.shipping_address ? (
                          <div className="font-sans text-sm space-y-1">
                            <p>{order.shipping_address.address_line1}</p>
                            {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                            <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.pincode}</p>
                            <p>{order.shipping_address.country}</p>
                          </div>
                        ) : <p className="text-sm text-muted-foreground">No address provided</p>}
                        <div className="mt-3 font-sans text-sm space-y-1">
                          <p><span className="text-muted-foreground">Email:</span> {order.customer_email}</p>
                          <p><span className="text-muted-foreground">Phone:</span> {order.customer_phone}</p>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Payment Details</h4>
                        <div className="font-sans text-sm space-y-1 mb-4">
                          <p><span className="text-muted-foreground">Subtotal:</span> {formatPrice(order.subtotal)}</p>
                          <p><span className="text-muted-foreground">Shipping:</span> {formatPrice(order.shipping_cost)}</p>
                          <p className="font-medium text-base"><span className="text-muted-foreground">Total:</span> {formatPrice(order.total)}</p>
                          {order.razorpay_payment_id && <p className="text-xs text-muted-foreground mt-2">Payment ID: {order.razorpay_payment_id}</p>}
                        </div>
                        <h4 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Update Status</h4>
                        <select value={order.order_status} onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                          disabled={updatingStatus === order.id} data-testid={`status-select-${order.id}`}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent">
                          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination-controls">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="pagination-prev">
              <ChevronLeft size={16} />
            </Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) pageNum = i + 1;
              else if (page <= 4) pageNum = i + 1;
              else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
              else pageNum = page - 3 + i;
              return (
                <Button key={pageNum} variant={page === pageNum ? "default" : "outline"} size="sm"
                  onClick={() => setPage(pageNum)} data-testid={`pagination-page-${pageNum}`}
                  className="min-w-[36px]">
                  {pageNum}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="pagination-next">
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;
